// src/pages/admin/audit/AuditLog.js
import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, startAfter, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';

const ACTION_OPTIONS = [
  'user.approved','user.rejected','user.frozen','user.unfrozen','user.deleted',
  'order.cancelled','order.dispute_resolved','order.escrow_released','order.refunded',
  'payout.held','payout.triggered',
];

export default function AuditLog() {
  const [logs,     setLogs]    = useState([]);
  const [lastDoc,  setLastDoc] = useState(null);
  const [hasMore,  setHasMore] = useState(true);
  const [loading,  setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionFilter, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  async function load(reset = false) {
    setLoading(true);
    setLoadError('');
    try {
      let constraints = [orderBy('timestamp', 'desc'), limit(50)];

      if (actionFilter) constraints = [where('action','==',actionFilter), ...constraints];
      if (dateFrom)    constraints = [where('timestamp','>=', Timestamp.fromDate(new Date(dateFrom))), ...constraints];
      if (dateTo)      constraints = [where('timestamp','<=', Timestamp.fromDate(new Date(dateTo + 'T23:59:59'))), ...constraints];

      let q = query(collection(db, 'auditLogs'), ...constraints);
      if (!reset && lastDoc) q = query(q, startAfter(lastDoc));

      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLogs(prev => reset ? docs : [...prev, ...docs]);
      setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
      setHasMore(snap.docs.length === 50);
    } catch (e) {
      setLoadError('Kunne ikke laste audit log. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(true); }, [actionFilter, dateFrom, dateTo]);

  function entityLink(log) {
    if (log.entityType === 'user')  return `/admin/users/${log.entityId}`;
    if (log.entityType === 'order') return `/admin/orders/${log.entityId}`;
    return null;
  }

  return (
    <div style={{ padding:isMobile?16:24, flex:1, overflowY:'auto' }}>
      <h2 style={{ margin:'0 0 16px', fontSize:18, fontWeight:700 }}>Audit Log</h2>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        <select value={actionFilter} onChange={e => setAction(e.target.value)} style={selectStyle}>
          <option value="">Alle handlinger</option>
          {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={inputStyle} />
        <button onClick={() => { setAction(''); setDateFrom(''); setDateTo(''); }} style={{ padding:'6px 12px', border:`1px solid ${COLORS.border}`, borderRadius:6, background:'transparent', color:COLORS.textDark, cursor:'pointer', fontSize:13 }}>
          Nullstill
        </button>
      </div>

      {loadError && <p style={{ color:COLORS.dangerText, fontSize:13, marginBottom:12 }}>{loadError}</p>}

      {/* Table */}
      <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', minWidth:720, borderCollapse:'collapse', fontSize:13 }}>
        <thead>
          <tr style={{ borderBottom:`2px solid ${COLORS.border}` }}>
            {['Tidspunkt','Utført av','Handling','Entitet','Begrunnelse'].map(h =>
              <th key={h} style={{ padding:'8px 10px', color:COLORS.textLight, fontWeight:600, textTransform:'uppercase', fontSize:11, textAlign:'left' }}>{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {logs.map(log => {
            const link = entityLink(log);
            return (
              <tr key={log.id} style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                <td style={{ padding:'10px', color:COLORS.textLight, fontSize:12, whiteSpace:'nowrap' }}>
                  {log.timestamp?.toDate?.().toLocaleString('nb-NO') ?? '—'}
                </td>
                <td style={{ padding:'10px' }}>{log.adminName ?? log.adminId}</td>
                <td style={{ padding:'10px' }}>
                  <span style={{ background:COLORS.surfaceHover, color:COLORS.textDark, padding:'2px 8px', borderRadius:4, fontSize:12, fontFamily:'monospace' }}>
                    {log.action}
                  </span>
                </td>
                <td style={{ padding:'10px' }}>
                  {link
                    ? <button onClick={() => navigate(link)} style={{ background:'none', border:'none', color:COLORS.blue, cursor:'pointer', fontSize:13, padding:0, fontFamily:'monospace' }}>{log.entityId.slice(0,10)}…</button>
                    : <span style={{ fontFamily:'monospace', fontSize:12 }}>{log.entityId}</span>
                  }
                </td>
                <td style={{ padding:'10px', color:COLORS.textMid, maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {log.reason}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {loading && <p style={{ color:COLORS.textLight, padding:'12px 0', fontSize:13 }}>Laster…</p>}
      {!loading && logs.length === 0 && <p style={{ color:COLORS.textLight, padding:'24px 0', fontSize:13 }}>Ingen loggposter funnet</p>}
      {hasMore && !loading && (
        <button onClick={() => load(false)} style={{ marginTop:12, padding:'8px 16px', border:`1px solid ${COLORS.border}`, borderRadius:6, background:'transparent', color:COLORS.textDark, cursor:'pointer', fontSize:13 }}>
          Last inn flere
        </button>
      )}
    </div>
  );
}

const selectStyle = { padding:'7px 10px', border:`1px solid ${COLORS.border}`, borderRadius:6, fontSize:13, background:COLORS.white, color:COLORS.textDark };
const inputStyle  = { padding:'7px 10px', border:`1px solid ${COLORS.border}`, borderRadius:6, fontSize:13, background:COLORS.white, color:COLORS.textDark };
